import { Inject, Injectable, PLATFORM_ID, signal, Signal } from '@angular/core';
import { isPlatformBrowser } from '@angular/common';

export type ThemePreference = 'light' | 'dark' | 'system';
export type ResolvedTheme = 'light' | 'dark';

const STORAGE_KEY = 'theme';
const SUPPORTED: ThemePreference[] = ['light', 'dark', 'system'];
const DEFAULT_PREFERENCE: ThemePreference = 'system';
const META_THEME_COLOR = { light: '#2563eb', dark: '#0f172a' } as const;

/**
 * Gerencia o tema visual (claro/escuro/sistema). SSR-safe.
 * Espelha o padrão de LanguageService: localStorage + APP_INITIALIZER + fallback ao SO.
 * Ver specs/002-dark-mode/contracts/theme-service.md
 */
@Injectable({ providedIn: 'root' })
export class ThemeService {
  private readonly isBrowser: boolean;
  private _preference: ThemePreference = DEFAULT_PREFERENCE;
  private readonly _resolved = signal<ResolvedTheme>('light');
  private mediaQuery: MediaQueryList | null = null;

  constructor(@Inject(PLATFORM_ID) platformId: object) {
    this.isBrowser = isPlatformBrowser(platformId);
  }

  /** Preferência atual escolhida pelo usuário (default 'system'). */
  get preference(): ThemePreference {
    return this._preference;
  }

  /** Tema efetivamente aplicado ('light' | 'dark'), reativo. */
  get resolvedTheme(): Signal<ResolvedTheme> {
    return this._resolved.asReadonly();
  }

  /**
   * Inicializa o tema. Chamado no APP_INITIALIZER.
   * Idempotente com o script inline de index.html (mesma fonte de leitura).
   */
  init(): void {
    this._preference = this.readStoredPreference();
    if (this.isBrowser) {
      this.mediaQuery = window.matchMedia('(prefers-color-scheme: dark)');
      this.mediaQuery.addEventListener('change', this.onSystemChange);
    }
    this.applyResolved();
  }

  /** Troca a preferência em runtime, persiste e aplica imediatamente. */
  setPreference(pref: ThemePreference): void {
    const normalized = SUPPORTED.includes(pref) ? pref : DEFAULT_PREFERENCE;
    this._preference = normalized;
    if (this.isBrowser) {
      try {
        localStorage.setItem(STORAGE_KEY, normalized);
      } catch {
        /* localStorage indisponível — segue sem persistir (FR-023) */
      }
    }
    this.applyResolved();
  }

  /** Conveniência: alterna light → dark → system → light. */
  cyclePreference(): void {
    const order: ThemePreference[] = ['light', 'dark', 'system'];
    const next = order[(order.indexOf(this._preference) + 1) % order.length];
    this.setPreference(next);
  }

  // --- internos ---

  private readonly onSystemChange = (): void => {
    if (this._preference === 'system') {
      this.applyResolved();
    }
  };

  private resolve(pref: ThemePreference): ResolvedTheme {
    if (pref === 'light' || pref === 'dark') {
      return pref;
    }
    // system
    if (this.isBrowser) {
      const mq = this.mediaQuery ?? window.matchMedia('(prefers-color-scheme: dark)');
      return mq.matches ? 'dark' : 'light';
    }
    return 'light';
  }

  private applyResolved(): void {
    const resolved = this.resolve(this._preference);
    this._resolved.set(resolved);
    this.applyToDom(resolved);
  }

  private applyToDom(resolved: ResolvedTheme): void {
    if (!this.isBrowser || typeof document === 'undefined') {
      return;
    }
    const root = document.documentElement;
    root.classList.toggle('dark', resolved === 'dark');
    root.setAttribute('data-theme', resolved);
    const meta = document.querySelector('meta[name="theme-color"]');
    if (meta) {
      meta.setAttribute('content', META_THEME_COLOR[resolved]);
    }
  }

  private readStoredPreference(): ThemePreference {
    if (!this.isBrowser) {
      return DEFAULT_PREFERENCE;
    }
    try {
      const value = localStorage.getItem(STORAGE_KEY);
      return value && SUPPORTED.includes(value as ThemePreference)
        ? (value as ThemePreference)
        : DEFAULT_PREFERENCE;
    } catch {
      return DEFAULT_PREFERENCE;
    }
  }
}
